import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useMsal, useIsAuthenticated, AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react'
import { InteractionStatus } from '@azure/msal-browser'
import { useEffect, useState } from 'react'
import { api } from './lib/api'
import { isDemoMode } from './lib/demo/flag'
import Layout from './components/Layout'
import PublicLayout from './components/PublicLayout'
import Dashboard from './pages/Dashboard'
import ConfigurationDrift from './pages/ConfigurationDrift'
import UserInsights from './pages/UserInsights'
import Tenants from './pages/Tenants'
import TenantManagement from './pages/TenantManagement'
import UserManagement from './pages/UserManagement'
import AdminQueue from './pages/AdminQueue'
import AdminErrors from './pages/AdminErrors'
import AdminQaValidation from './pages/AdminQaValidation'
import SecurityCenter from './pages/SecurityCenter'
import ExecutiveReport from './pages/ExecutiveReport'
import Settings from './pages/Settings'
import Help from './pages/Help'
import DocsIndex from './pages/docs/DocsIndex'
import OnboardingGuide from './pages/docs/OnboardingGuide'
import PermissionsGuide from './pages/docs/PermissionsGuide'
import AdminConsentCallback from './pages/AdminConsentCallback'
import SignUp from './pages/signup/SignUp'
import SignUpCallback from './pages/signup/SignUpCallback'
import SignUpSuccess from './pages/signup/SignUpSuccess'
import { loginRequest } from './lib/auth'
import { useTheme } from './context/ThemeContext'

// Guardian Admin Route Protection - uses API-based role check
function GuardianAdminRoute({ children }: { children: React.ReactNode }) {
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null)

  useEffect(() => {
    let cancelled = false
    api.getCurrentUser()
      .then((userInfo) => {
        if (!cancelled) setIsAdmin(userInfo?.isGlobalAdmin === true)
      })
      .catch(() => {
        // Secure by default: if API fails, deny access
        if (!cancelled) setIsAdmin(false)
      })
    return () => { cancelled = true }
  }, [])

  // While loading, show nothing (prevents flash of protected content)
  if (isAdmin === null) return null

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

function LoginPage() {
  const { instance, inProgress } = useMsal()
  const { theme } = useTheme()
  const heroLogoSrc = theme === 'light' ? '/iuvo-logo-on-light.png' : '/iuvo-logo-on-dark.png'

  const handleLogin = () => {
    instance.loginRedirect(loginRequest).catch(e => {
      console.error('Login failed:', e)
    })
  }

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-dark-900 px-4 overflow-hidden">
      {/* Brand-aligned ambient gradient: Soft Green halo top-left, Horizon Blue cool bottom-right */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-br from-primary-500/10 via-transparent to-secondaryBlue-300/5"
      />
      {/* Subtle grid pattern reinforces the brand body texture */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-40 bg-grid-pattern bg-grid"
      />

      <div className="relative max-w-lg w-full bg-dark-800 p-12 rounded-2xl shadow-2xl border border-dark-700">
        <div>
          {/* iuvo Guardian Logo */}
          <div className="flex justify-center mb-6">
            <img
              src={heroLogoSrc}
              alt="iuvo Guardian"
              className="h-20 w-auto max-w-full"
            />
          </div>

          <div className="text-center mb-8">
            {/* Heading intentionally inherits color from body so it flips with the theme:
                slate-100 in dark mode, slate-900 in light mode (see index.css). */}
            <h1 className="text-2xl font-bold mb-2">
              Welcome Back
            </h1>
            <p className="text-sm text-slate-400">
              Microsoft 365 Security &amp; Compliance Platform
            </p>
          </div>
        </div>

        <div className="space-y-6">
          <button
            onClick={handleLogin}
            disabled={inProgress === InteractionStatus.Login}
            className="group relative w-full flex justify-center py-4 px-6 border border-transparent text-base font-semibold rounded-lg text-white bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-500 hover:to-primary-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-dark-800 focus:ring-primary-500 disabled:opacity-50 transition-all duration-200 transform hover:scale-[1.02] shadow-glow-cyan hover:shadow-glow-cyan-lg"
          >
            <span className="absolute left-0 inset-y-0 flex items-center pl-4">
              <img
                src="/iuvo-icon-white.png"
                alt=""
                aria-hidden="true"
                className="h-6 w-6 opacity-90 group-hover:opacity-100"
              />
            </span>
            {inProgress === InteractionStatus.Login ? 'Signing in...' : 'Sign in with Microsoft'}
          </button>

          <p className="text-center text-xs text-slate-500">
            Secure authentication via Microsoft Entra ID
          </p>
        </div>

        <div className="text-center mt-6">
          <p className="text-xs text-slate-500">
            &copy; {new Date().getFullYear()} iuvo Guardian. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  )
}

function App() {
  const location = useLocation()

  if (isDemoMode) {
    // Public demo: no MSAL, no auth template, no admin gate. Just the
    // routes a prospective customer would actually click through.
    return (
      <Layout>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/configuration-drift" element={<ConfigurationDrift />} />
          <Route path="/drift" element={<Navigate to="/configuration-drift" replace />} />
          <Route path="/user-insights" element={<UserInsights />} />
          <Route path="/security" element={<SecurityCenter />} />
          <Route path="/executive-report" element={<ExecutiveReport />} />
          <Route path="/help" element={<Help />} />
          {/* Anything else (admin, settings, signup, docs) sends visitors
              back to the dashboard — those views need real backends. */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Layout>
    )
  }

  const isAuthenticated = useIsAuthenticated()
  const { instance, accounts } = useMsal()

  useEffect(() => {
    if (isAuthenticated && accounts.length > 0) {
      // Set the active account
      instance.setActiveAccount(accounts[0])
    }
  }, [isAuthenticated, accounts, instance])

  // Public routes bypass auth entirely - evaluated first
  if (location.pathname.startsWith('/docs')) {
    return (
      <Routes>
        <Route path="/docs" element={<PublicLayout />}>
          <Route index element={<DocsIndex />} />
          <Route path="onboarding" element={<OnboardingGuide />} />
          <Route path="permissions" element={<PermissionsGuide />} />
        </Route>
      </Routes>
    )
  }

  // Allow unauthenticated access to admin consent callback
  // This page is for external customer admins who have just granted consent
  // Security is handled via encrypted state parameter validation
  if (location.pathname === '/admin-consent-callback') {
    return (
      <Routes>
        <Route path="/admin-consent-callback" element={<AdminConsentCallback />} />
      </Routes>
    )
  }

  // Allow unauthenticated access to self-service sign-up pages
  // These pages handle the public onboarding flow for new customers
  if (location.pathname.startsWith('/signup')) {
    return (
      <Routes>
        <Route path="/signup" element={<SignUp />} />
        <Route path="/signup/callback" element={<SignUpCallback />} />
        <Route path="/signup/success" element={<SignUpSuccess />} />
      </Routes>
    )
  }

  // All other routes go through existing MSAL auth pattern
  return (
    <>
      <UnauthenticatedTemplate>
        <LoginPage />
      </UnauthenticatedTemplate>

      <AuthenticatedTemplate>
        <Layout>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/configuration-drift" element={<ConfigurationDrift />} />
            <Route path="/drift" element={<Navigate to="/configuration-drift" replace />} />
            <Route path="/user-insights" element={<UserInsights />} />
            {/* Security Center - unified security posture hub */}
            <Route path="/security" element={<SecurityCenter />} />
            <Route path="/executive-report" element={<ExecutiveReport />} />
            {/* Legacy routes redirect to Security Center tabs for backward compatibility */}
            <Route path="/compliance" element={<Navigate to="/security?tab=secure-score" replace />} />
            <Route path="/compliance/standards" element={
              <Navigate to="/security?tab=best-practices" replace />
            } />
            {/* Legacy routes redirect to Baseline Compliance tabs */}
            <Route path="/baseline-reports" element={<Navigate to="/configuration-drift?tab=export" replace />} />
            <Route path="/tenants" element={<Tenants />} />
            <Route path="/tenant-management" element={
              <GuardianAdminRoute>
                <TenantManagement />
              </GuardianAdminRoute>
            } />
            <Route path="/user-management" element={
              <GuardianAdminRoute>
                <UserManagement />
              </GuardianAdminRoute>
            } />
            <Route path="/admin/queue" element={
              <GuardianAdminRoute>
                <AdminQueue />
              </GuardianAdminRoute>
            } />
            <Route path="/admin/errors" element={
              <GuardianAdminRoute>
                <AdminErrors />
              </GuardianAdminRoute>
            } />
            <Route path="/admin/qa-validation" element={
              <GuardianAdminRoute>
                <AdminQaValidation />
              </GuardianAdminRoute>
            } />
            <Route path="/config-as-code" element={<Navigate to="/configuration-drift?tab=pr-tracker" replace />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/help" element={<Help />} />
            <Route path="/admin-consent-callback" element={<AdminConsentCallback />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Layout>
      </AuthenticatedTemplate>
    </>
  )
}

export default App